import { useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { siteStats } from "@/mocks/site";

export default function HeroSection() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");

  const onSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = keyword.trim();
    navigate(trimmed ? `/archive?q=${encodeURIComponent(trimmed)}` : "/archive");
  };

  return (
    <section className="relative w-full overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=dark%20abandoned%20archive%20room%20with%20tall%20shelves%20of%20old%20documents%20and%20a%20single%20warm%20amber%20lamp%20casting%20long%20shadows%2C%20moody%20cinematic%20atmosphere%2C%20deep%20charcoal%20and%20ochre%20tones%2C%20atmospheric%20fog%2C%20editorial%20photography&width=1600&height=900&seq=arch-hero-midnight&orientation=landscape"
          alt="深夜档案室场景，用于网站首屏背景"
          title="深夜黑料 事件复盘档案库"
          className="h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background-50/80 via-background-50/72 to-background-50" />
        <div className="absolute inset-0 bg-gradient-to-r from-background-50/85 via-background-50/40 to-transparent" />
      </div>

      <div className="relative mx-auto max-w-content px-4 pb-14 pt-16 md:px-6 md:pb-20 md:pt-24">
        <div className="max-w-3xl animate-fade-up">
          <p className="flex items-center gap-3 text-xs tracking-[0.34em] text-accent-400">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary-500" />
            深夜档案 · 第 214 期已归档
          </p>

          <h1 className="mt-6">
            <span className="block font-heading text-xs tracking-[0.5em] text-foreground-500">
              MIDNIGHT ARCHIVE
            </span>
            <span className="mt-3 block font-heading text-4xl font-black leading-tight text-foreground-950 md:text-6xl">
              深夜黑料
            </span>
          </h1>

          <p className="mt-5 font-heading text-xl text-foreground-800 md:text-2xl">
            把旧账翻出来，重新捋一遍。
          </p>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-foreground-600 md:text-base">
            这里汇总深夜黑料考古回顾的完整时间线、事件细节与前后因果，帮你快速理清来龙去脉。想找旧事重提的讨论焦点，或补上之前漏掉的深挖内容，直接从这里入手即可。
          </p>

          <form onSubmit={onSearch} className="mt-8 flex w-full max-w-xl flex-col gap-3 sm:flex-row" role="search">
            <div className="relative flex-1">
              <i className="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-lg text-foreground-500" />
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜事件、人物动向或时间线关键词"
                aria-label="检索复盘档案"
                className="w-full rounded-md border border-background-300/70 bg-background-50/80 py-3 pl-11 pr-4 text-sm text-foreground-900 placeholder:text-foreground-500 outline-none backdrop-blur transition-colors duration-200 focus:border-accent-500"
              />
            </div>
            <button
              type="submit"
              className="whitespace-nowrap rounded-md bg-primary-600 px-6 py-3 text-sm font-medium text-foreground-950 transition-colors duration-200 hover:bg-primary-500 cursor-pointer"
            >
              开始考古
            </button>
          </form>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <span className="text-xs text-foreground-500">热门检索</span>
            {["选秀票数", "电影改档", "版权归属", "剪辑争议"].map((tag) => (
              <Link
                key={tag}
                to={`/archive?q=${encodeURIComponent(tag)}`}
                className="whitespace-nowrap rounded-full border border-background-300/70 px-3 py-1 text-xs text-foreground-700 transition-colors duration-200 hover:border-accent-500 hover:text-accent-400 cursor-pointer"
              >
                {tag}
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-14 grid grid-cols-2 gap-4 md:mt-20 md:grid-cols-4 md:gap-6">
          {siteStats.map((stat) => (
            <div
              key={stat.label}
              className="rounded-lg border border-background-200/70 bg-background-100/70 px-4 py-5 backdrop-blur md:px-5 md:py-6"
            >
              <p className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                {stat.value}
              </p>
              <p className="mt-2 text-xs text-foreground-700 md:text-sm">{stat.label}</p>
              <p className="mt-1 text-[11px] text-foreground-500">{stat.note}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}